import { ArrowDropDown, ArrowDropUp, Cancel } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Divider,
  Fade,
  FormControl,
  Grid,
  IconButton,
  Input,
  MenuItem,
  Popper,
  Select,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { ArrowDropDownIcon, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FocusEventHandler, useEffect, useRef, useState } from "react";
import { SelectDatePicker } from "./SelectDatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  homepageActions,
  selectDistricts,
  selectGenres,
  selectScenarios,
} from "../saga/homepageSlice";
import { ISearchFilter } from "features/searchStore/models";
import { searchStoreActions } from "features/searchStore/saga/searchStoreSlice";
import { IDistrictData, IKeywordSuggest } from "../models";
import { useNavigate } from "react-router-dom";
import LabelCheckbox from "features/searchStore/components/LabelCheckbox";
import { IValueLabel } from "utils/commonModels";
import { homepageApi } from "../homepageApi";
import { generateTimeOptions } from "helpers/generateTimeOptions";
import { generatePeopleNumberOpts } from "helpers/generatePeopleNumerOption";
import { generateBudgetOptions } from "helpers/generateBudgetOptions";

function SearchBar() {
  const todayDate = new Date();
  const searchBarRef = useRef<HTMLDivElement | null>(null);
  const selectTimeRef = useRef<HTMLSelectElement>(null);
  const timeMenuRef = useRef<HTMLDivElement | null>(null);
  const selectPeopleRef = useRef<HTMLSelectElement | null>(null);
  const peopleMenuRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const districtList = useAppSelector(selectDistricts);
  const genreList = useAppSelector(selectGenres);
  const scenarioList = useAppSelector(selectScenarios);

  const [selectedArea, setSelectedArea] = useState<IValueLabel<string>>();
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [genresStr, setGenresStr] = useState<string[]>([]);
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);
  const [minValue, setMinValue] = useState<number>(0);
  const [maxValue, setMaxValue] = useState<number>(0);

  const [tabValue, setTabValue] = useState(0);
  const [openArea, setOpenArea] = useState(false);
  const [openGenre, setOpenGenre] = useState(false);
  const [openCategory, setOpenCategory] = useState(false);
  const [openSelect, setOpenSelect] = useState(false);

  const [dateValue, setDateValue] = useState<Dayjs | null>(
    dayjs(todayDate).locale("ja")
  );

  const [openTime, setOpenTime] = useState(false);
  const [openPeople, setOpenPeople] = useState(false);
  const [timeValue, setTimeValue] = useState<string | null>("19:00");
  const [customerNumber, setCustomerNumber] = useState<number | null>(2);

  const [loading, setLoading] = useState(false);
  const [keyword, setKeyWord] = useState<IValueLabel<string>>({
    value: "",
    label: "",
  });
  const [keywordSend, setKeyWordSend] = useState<string>("");
  const [suggests, setSuggests] = useState<IKeywordSuggest[]>([]);

  const handleChangeTab = (e: any, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenArea = () => {
    setOpenArea(!openArea);
  };

  const handleTimeFocus = () => {
    const container = timeMenuRef.current?.firstChild as HTMLDivElement;
    const focusedItem = container?.querySelector(
      '[aria-selected="true"]'
    ) as HTMLDivElement;

    if (focusedItem && container && timeMenuRef.current) {
      const total = focusedItem.offsetTop - timeMenuRef.current.offsetTop;

      timeMenuRef.current.scrollTop = total;
    }
  };

  const handlePeopleFocus = () => {
    const container = peopleMenuRef.current?.firstChild as HTMLDivElement;
    const focusedItem = container?.querySelector(
      '[aria-selected="true"]'
    ) as HTMLDivElement;

    if (focusedItem && container && peopleMenuRef.current) {
      const total = focusedItem.offsetTop - peopleMenuRef.current.offsetTop;

      peopleMenuRef.current.scrollTop = total;
    }
  };

  const handleOpenGenre = () => {
    setOpenGenre(true);
  };

  const handleOpenCategory = () => {
    setOpenCategory(!openCategory);
  };

  const handleSelectedDistrict = (e: IDistrictData) => {
    setSelectedArea({
      value: e.PK,
      label: e.Name,
    });
    setOpenArea(false);
  };

  const handleSelectGenre = (value: any, label: any) => {
    if (!selectedGenres.includes(value)) {
      setSelectedGenres((pre) => [...pre, value]);
      setGenresStr((pre) => [...pre, label]);
    } else {
      setSelectedGenres(selectedGenres.filter((id) => id !== value));
      setGenresStr(genresStr.filter((id) => id !== label));
    }
    // setOpenGenre(false);
  };

  const handleSelectScenarios = (value: any, label: any) => {
    if (!selectedScenarios.includes(value)) {
      setSelectedScenarios((pre) => [...pre, value]);
    } else {
      setSelectedScenarios(selectedScenarios.filter((id) => id !== value));
    }
    // setOpenCategory(false);
  };

  const handleClearGenre = () => {
    setSelectedGenres([]);
    setGenresStr([]);
  };

  const handleClearScenarios = () => {
    setSelectedScenarios([]);
    setMinValue(0);
    setMaxValue(0);
  };

  const handleKeywordSearchChange = (e: any) => {
    setKeyWord(e.currentTarget.value);
    setKeyWordSend(e.currentTarget.value);
  };

  const handleSelectSuggest = (e: IValueLabel<string>) => {
    setKeyWord(e);
  };

  const handleSearch = () => {
    const newFilter: ISearchFilter = {
      area: selectedArea,
      keyword: keyword,
      sort_code: 0,
      date: dateValue,
      time: timeValue,
      people: customerNumber,
      minBudget: minValue,
      maxBudget: maxValue,
      genres: selectedGenres,
      scenes: selectedScenarios,
      conditions: [],
    };
    dispatch(searchStoreActions.setSearchFilter(newFilter));
    navigate("/stores");
  };

  useEffect(() => {
    setLoading(true);
    keywordSend !== "" &&
      homepageApi
        .getSuggets({
          area_keyword: "",
          keyword: keywordSend,
          threshold: 0.4,
        })
        .then((e) => setSuggests(e.data.body.data))
        .catch((error) => console.log(error));
    setLoading(false);
  }, [keywordSend]);

  useEffect(() => {
    setLoading(true);
    dispatch(homepageActions.getDistricts(0));
    setLoading(false);
  }, [dispatch]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
      <Box
        display="flex"
        flex={1}
        borderRadius={2}
        bgcolor="white"
        position="relative"
        flexDirection="column"
        py={4}
        px={10}
      >
        <Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleChangeTab}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="日付・エリアから探す" value={0} />
            <Tab label="キーワードから探す" value={1} />
          </Tabs>
        </Box>

        <Box ref={searchBarRef} display="flex" alignItems="center">
          {tabValue === 0 && (
            <Box
              display="flex"
              bgcolor="white"
              flex={1}
              border={1}
              borderRadius={1}
              borderColor="divider"
            >
              <Box
                display="flex"
                alignItems="center"
                p={2}
                flex={1}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    opacity: 0.4,
                  },
                }}
                onClick={handleOpenArea}
              >
                <Typography
                  sx={{
                    display: "flex",
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {selectedArea?.label ?? "東京都 (全域)"}
                </Typography>
                {openArea ? <ArrowDropUp /> : <ArrowDropDown />}
              </Box>

              <Divider flexItem orientation="vertical" />
              <SelectDatePicker
                value={dateValue}
                onChange={(newValue) => setDateValue(newValue)}
              />
              <Divider flexItem orientation="vertical" />

              <Select
                ref={selectTimeRef}
                value={timeValue}
                variant="standard"
                onChange={(e) => setTimeValue(e.target.value)}
                // open={openTime}
                // onClick={() => setOpenTime(true)}
                // onClose={() => setOpenTime(false)}
                disableUnderline
                MenuProps={{
                  onTransitionEnter: handleTimeFocus,
                  PaperProps: {
                    ref: timeMenuRef,
                    style: {
                      maxHeight: "200px",
                    },
                  },
                  MenuListProps: {
                    sx: {
                      "& .MuiMenuItem-root:focus": {
                        // scrollMarginTop: "0px",
                        // bgcolor: 'black'
                      },
                    },
                  },
                  disableScrollLock: true,
                }}
                sx={{
                  width: "100px",
                  minWidth: "100px",
                  cursor: "pointer",
                  "&:hover": {
                    opacity: 0.4,
                  },
                }}
                SelectDisplayProps={{
                  style: {
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    backgroundColor: 'transparent'
                  },
                }}
              >
                {generateTimeOptions(7, 24)}
              </Select>

              <Divider flexItem orientation="vertical" />

              <Select
                ref={selectPeopleRef}
                value={customerNumber}
                placeholder="2名"
                variant="standard"
                disableUnderline
                autoFocus
                onChange={(e) => setCustomerNumber(e.target.value as number)}
                sx={{
                  width: "100px",
                  minWidth: "100px",
                  cursor: "pointer",
                  "&:hover": {
                    opacity: 0.4,
                  },
                }}
                SelectDisplayProps={{
                  style: {
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    backgroundColor: 'transparent'
                  },
                }}
                MenuProps={{
                  onTransitionEnter: handlePeopleFocus,
                  PaperProps: {
                    ref: peopleMenuRef,
                    style: {
                      maxHeight: "200px",
                    },
                  },
                  disableScrollLock: true,
                }}
              >
                {generatePeopleNumberOpts(1, 99)}
              </Select>

              <Divider flexItem orientation="vertical" />

              <Box
                display="flex"
                alignItems="center"
                p={1}
                flex={1}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    opacity: 0.4,
                  },
                }}
                onClick={handleOpenGenre}
              >
                <Box display="flex" flex={1}>
                  {selectedGenres.length === 0 ? (
                    <Typography
                      sx={{
                        display: "flex",
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      料理ジャンル
                    </Typography>
                  ) : selectedGenres.length === 1 ? (
                    <Typography
                      sx={{
                        display: "flex",
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {genresStr[0]}
                    </Typography>
                  ) : (
                    <>
                      <Typography
                        sx={{
                          display: "flex",
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        料理ジャンル
                      </Typography>
                      <Avatar
                        sx={{
                          bgcolor: "primary.main",
                          fontSize: "small",
                          height: "20px",
                          width: "20px",
                        }}
                      >
                        {selectedGenres.length}
                      </Avatar>
                    </>
                  )}
                </Box>
                {openGenre ? <ArrowDropUp /> : <ArrowDropDown />}
              </Box>

              <Divider flexItem orientation="vertical" />

              <Box
                display="flex"
                alignItems="center"
                p={1}
                flex={1}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    opacity: 0.4,
                  },
                }}
                onClick={handleOpenCategory}
              >
                <Box display="flex" flex={1}>
                  {selectedScenarios.length +
                    +!!(minValue !== 0) +
                    +!!(maxValue !== 0) ===
                  0 ? (
                    <Typography
                      sx={{
                        display: "flex",
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      こだわり
                    </Typography>
                  ) : (
                    <>
                      <Typography
                        sx={{
                          display: "flex",
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        こだわり
                      </Typography>
                      <Avatar
                        sx={{
                          bgcolor: "primary.main",
                          fontSize: "small",
                          height: "20px",
                          width: "20px",
                        }}
                      >
                        {selectedScenarios.length +
                          +!!(minValue !== 0) +
                          +!!(maxValue !== 0)}
                      </Avatar>
                    </>
                  )}
                </Box>
                {openCategory ? <ArrowDropUp /> : <ArrowDropDown />}
              </Box>
            </Box>
          )}

          {tabValue === 1 && (
            <Box
              display="flex"
              bgcolor="white"
              flex={1}
              border={1}
              borderRadius={1}
              borderColor="divider"
            >
              <Input
                placeholder="キーワード"
                value={keyword.label}
                onChange={handleKeywordSearchChange}
                onBlur={() => {
                  setSuggests([]);
                  setKeyWordSend("");
                }}
                sx={{
                  p: 2,
                  flex: 4,
                  borderRight: 1,
                  borderColor: "divider",
                  ":before": {
                    borderBottomWidth: "0",
                  },
                  ":after": {
                    borderBottomWidth: "5px",
                    // borderBottomColor: 'red',
                  },
                }}
              />
              <Divider flexItem orientation="vertical" />

              <Box
                display="flex"
                alignItems="center"
                p={1}
                flex={1}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    opacity: 0.4,
                  },
                }}
                onClick={handleOpenCategory}
              >
                <Box display="flex" flex={1}>
                  {selectedScenarios.length +
                    +!!(minValue !== 0) +
                    +!!(maxValue !== 0) ===
                  0 ? (
                    <Typography>こだわり</Typography>
                  ) : (
                    <>
                      <Typography sx={{ display: "flex", flex: 1 }}>
                        こだわり
                      </Typography>
                      <Avatar
                        sx={{
                          bgcolor: "primary.main",
                          fontSize: "small",
                          height: "20px",
                          width: "20px",
                        }}
                      >
                        {selectedScenarios.length +
                          +!!(minValue !== 0) +
                          +!!(maxValue !== 0)}
                      </Avatar>
                    </>
                  )}
                </Box>
                {openCategory ? <ArrowDropUp /> : <ArrowDropDown />}
              </Box>
            </Box>
          )}

          <Button
            variant="contained"
            sx={{
              marginLeft: "5%",
              px: "7%",
              py: 2,
              textTransform: "none",
              bgcolor: "orange",
              "&:hover": {
                bgcolor: "orange",
                opacity: 0.8,
              },
            }}
            onClick={handleSearch}
          >
            <Typography fontWeight="bold">検索</Typography>
          </Button>
        </Box>

        <Popper
          open={openArea}
          anchorEl={searchBarRef.current}
          placement="auto-start"
          transition
          disablePortal
          modifiers={[
            {
              name: "preventOverflow",
              enabled: true,
              options: {
                altAxis: true,
                altBoundary: true,
                tether: true,
                rootBoundary: "document",
                padding: 8,
              },
            },
          ]}
          style={{
            width: searchBarRef.current?.clientWidth,
          }}
          sx={{
            position: "relative",
            overflowX: "unset",
            overflowY: "unset",
            boxShadow: 1,
            zIndex: 5,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={() => setOpenArea(false)}>
              <Fade {...TransitionProps} timeout={250}>
                <Box
                  display="flex"
                  bgcolor="white"
                  zIndex={120}
                  width="100%"
                  maxHeight="500px"
                  flexWrap="wrap"
                >
                  {districtList &&
                    districtList.map((e) => (
                      <Box
                        key={e.PK}
                        border={1}
                        borderRadius={2}
                        borderColor="divider"
                        sx={{
                          padding: 1,
                          m: 1,
                          "&:hover": {
                            cursor: "pointer",
                            color: "primary.main",
                            borderColor: "primary.main",
                          },
                        }}
                        onClick={() => handleSelectedDistrict(e)}
                      >
                        <Typography fontSize="13px">{e.Name}</Typography>
                      </Box>
                    ))}
                </Box>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>

        <Popper
          open={openGenre}
          anchorEl={searchBarRef.current}
          placement="auto-start"
          transition
          disablePortal
          modifiers={[
            {
              name: "preventOverflow",
              enabled: true,
              options: {
                altAxis: true,
                altBoundary: true,
                tether: true,
                rootBoundary: "document",
                padding: 8,
              },
            },
          ]}
          style={{
            width: searchBarRef.current?.clientWidth,
          }}
          sx={{
            position: "relative",
            overflowX: "unset",
            overflowY: "unset",
            boxShadow: 1,
            zIndex: 5,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={() => setOpenGenre(false)}>
              <Fade {...TransitionProps} timeout={250}>
                <Box
                  display="flex"
                  bgcolor="white"
                  zIndex={120}
                  width="100%"
                  maxHeight="500px"
                  flexWrap="wrap"
                >
                  <Grid
                    container
                    zIndex={120}
                    p={2}
                    maxHeight="500px"
                    overflow="auto"
                  >
                    {genreList.map((e, index) => (
                      <LabelCheckbox
                        xs={2}
                        value={e.Id}
                        boxLabel={e.Name}
                        px={2}
                        onSelected={handleSelectGenre}
                        defaultState={selectedGenres.includes(e.Id)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            bgcolor: "rgba(0, 0, 0, 0.1)",
                          },
                        }}
                      />
                    ))}
                  </Grid>
                  <Box
                    display="flex"
                    justifyContent="end"
                    width="100%"
                    p={2}
                    borderColor="divider"
                  >
                    <Button
                      variant="outlined"
                      onClick={handleClearGenre}
                      sx={{ mx: 2, textTransform: "none" }}
                    >
                      リセット
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>

        <Popper
          open={openCategory}
          anchorEl={searchBarRef.current}
          placement="auto-start"
          transition
          disablePortal
          modifiers={[
            {
              name: "preventOverflow",
              enabled: true,
              options: {
                altAxis: true,
                altBoundary: true,
                tether: true,
                rootBoundary: "document",
                padding: 8,
              },
            },
          ]}
          style={{
            width: searchBarRef.current?.clientWidth,
          }}
          sx={{
            position: "relative",
            overflowX: "unset",
            overflowY: "unset",
            borderTop: 1,
            borderColor: "divider",
            boxShadow: 1,
            zIndex: 5,
          }}
        >
          {({ TransitionProps }) => (
            <ClickAwayListener
              onClickAway={() => {
                if (openSelect) return;
                setOpenCategory(false);
              }}
            >
              <Fade {...TransitionProps} timeout={250}>
                <Box
                  display="flex"
                  bgcolor="white"
                  zIndex={120}
                  width="100%"
                  maxHeight="500px"
                  flexWrap="wrap"
                >
                  <Grid
                    container
                    zIndex={120}
                    p={2}
                    maxHeight="500px"
                    overflow="auto"
                  >
                    <Grid item xs={2}>
                      <Typography>予算</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={10}
                      display="flex"
                      alignItems="center"
                      width="max-content"
                    >
                      <Typography>コース一人あたり</Typography>
                      <Box display="flex" p={1} flex={1} alignItems="center">
                        <FormControl size="small">
                          <Select
                            placeholder="Min"
                            value={minValue.toString()}
                            onChange={(e) =>
                              setMinValue(parseInt(e.target.value))
                            }
                            onOpen={() => setOpenSelect(true)}
                            onClose={() => setOpenSelect(false)}
                            fullWidth
                          >
                            <MenuItem value="0">下限なし</MenuItem>
                            {generateBudgetOptions()}
                          </Select>
                        </FormControl>
                        <Typography mx={2}>~</Typography>
                        <FormControl size="small">
                          <Select
                            placeholder="Max"
                            value={maxValue.toString()}
                            onChange={(e) =>
                              setMaxValue(parseInt(e.target.value))
                            }
                            onOpen={() => setOpenSelect(true)}
                            onClose={() => setOpenSelect(false)}
                            fullWidth
                          >
                            <MenuItem value="0">上限なし</MenuItem>
                            {generateBudgetOptions()}
                          </Select>
                        </FormControl>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                    </Grid>

                    <Grid item xs={2}>
                      <Typography>こだわり</Typography>
                    </Grid>

                    <Grid item xs={10} container>
                      {scenarioList.map((e, index) => (
                        <LabelCheckbox
                          xs={3}
                          value={e.Id}
                          boxLabel={e.Name}
                          px={2}
                          onSelected={handleSelectScenarios}
                          defaultState={selectedScenarios.includes(e.Id)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": {
                              bgcolor: "rgba(0, 0, 0, 0.1)",
                            },
                          }}
                        />
                      ))}
                    </Grid>
                  </Grid>
                  <Box
                    display="flex"
                    justifyContent="end"
                    width="100%"
                    p={2}
                    borderColor="divider"
                  >
                    <Button
                      variant="outlined"
                      onClick={handleClearScenarios}
                      sx={{ mx: 2, textTransform: "none" }}
                    >
                      リセット
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
      </Box>
      {suggests && suggests.length > 0 && (
        <Box p={3} bgcolor="#eee" zIndex={7}>
          {suggests.map((e) => (
            <>
              <Chip
                key={e.value}
                label={e.label}
                sx={{ m: 1, zIndex: 8 }}
                clickable
                onMouseDown={() => handleSelectSuggest(e)}
              />
            </>
          ))}
        </Box>
      )}
      {loading && (
        <Box p={3} bgcolor="#eee" zIndex={7} display="flex">
          <Skeleton
            variant="rounded"
            height="25px"
            width="50px"
            sx={{ m: 1 }}
          />
          <Skeleton
            variant="rounded"
            height="25px"
            width="70px"
            sx={{ m: 1 }}
          />
          <Skeleton
            variant="rounded"
            height="25px"
            width="30px"
            sx={{ m: 1 }}
          />
        </Box>
      )}
    </LocalizationProvider>
  );
}

export default SearchBar;
